import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2, Network, Handshake, Shield, Sparkles, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { markSlideViewed, getActivityProgress } from '../utils/progressTracker';

const ACTIVITY_ID = 'deeje-dc4';

const theme = {
  primary: '#2D6A4F',
  secondary: '#E76F51',
  bg: '#F5FBF7',
  text: '#2C3E50',
};

const bgImage = "https://customer-assets.emergentagent.com/job_9e595d24-b8ef-429a-8a8c-c23583fe1376/artifacts/uwszpqa9_image.png";

const slides = [
  {
    id: 1, type: 'title',
    title: "DC 4 – Dynamiques interinstitutionnelles, partenariats et réseaux",
    bloc7: "Bloc de compétences n° 7 : Connaissance et analyse des contextes institutionnels",
    bloc8: "Bloc de compétences n° 8 : Mobilisation des acteurs et partenaires"
  },
  {
    id: 2, type: 'sommaire',
    title: "Sommaire",
    sections: [
      { num: "1", title: "Introduction au DC4" },
      { num: "2", title: "Cadre juridique du DC4" },
      { num: "3", title: "4.1 Contribuer à l'analyse et à l'évolution des pratiques professionnelles" },
      { num: "4", title: "4.2 S'informer et se former pour faire évoluer ses pratiques" },
      { num: "5", title: "4.3 Décliner les orientations et les missions de la structure au travers du projet social, éducatif et pédagogique" },
      { num: "6", title: "4.4 Représenter le service, l'établissement, l'institution" },
      { num: "7", title: "4.5 Favoriser les dynamiques interinstitutionnelles et partenariales" },
      { num: "8", title: "4.6 Contribuer à l'articulation des actions menées par les acteurs partenaires et les institutions ressources" },
      { num: "9", title: "4.7 Développer une veille sur les évolutions des politiques liées au secteur de la petite enfance" },
      { num: "10", title: "4.8 Contribuer à des projets de développement de modes d'accueil" },
      { num: "11", title: "4.9 Conduire des groupes de travail sur des thématiques liées à la petite enfance" },
      { num: "12", title: "Partenaires incontournables" },
      { num: "13", title: "Synthèse DC4" },
    ]
  },
  // INTRODUCTION
  { id: 3, type: 'section', title: "Introduction au DC4" },
  {
    id: 4, type: 'content',
    title: "S'inscrire dans les dynamiques institutionnelles",
    content: "Le DC4 explore la capacité du professionnel à se situer dans son environnement institutionnel et territorial, à construire des partenariats et à contribuer à l'évolution des pratiques. Il ne s'agit pas d'un domaine annexe mais d'une dimension structurante de l'identité professionnelle.",
    bullets: [
      "Analyser et ajuster ses pratiques professionnelles",
      "S'engager dans une démarche de formation continue",
      "Décliner les orientations institutionnelles en actions concrètes",
      "Représenter la structure auprès des partenaires",
      "Favoriser les dynamiques interinstitutionnelles et de réseau"
    ]
  },
  // CADRE JURIDIQUE
  { id: 5, type: 'section', title: "Cadre juridique du DC4" },
  {
    id: 6, type: 'content',
    title: "Fondements réglementaires",
    content: "Les dynamiques institutionnelles et partenariales s'inscrivent dans un cadre réglementaire qui définit les missions, les obligations et les modalités de coopération entre les acteurs du secteur de la petite enfance.",
    bullets: [
      "Code de l'action sociale et des familles (CASF)",
      "Schéma départemental des services aux familles",
      "Convention d'objectifs et de gestion (COG) État-CAF",
      "Charte nationale de l'accueil du jeune enfant",
      "Plan interministériel pour la petite enfance",
      "Obligations de formation continue des professionnels"
    ]
  },
  // 4.1 CONTRIBUER À L'ANALYSE ET À L'ÉVOLUTION DES PRATIQUES
  { id: 7, type: 'section', title: "4.1 – Contribuer à l'analyse et à l'évolution des pratiques professionnelles" },
  {
    id: 8, type: 'content',
    title: "Contribuer à l'analyse et à l'évolution des pratiques professionnelles",
    content: "L'évolution des pratiques professionnelles constitue une exigence permanente dans le champ de la petite enfance. Les besoins des enfants, les attentes des familles et les cadres institutionnels évoluent continuellement. Le professionnel ne peut se contenter d'appliquer des méthodes figées ; il doit adopter une posture réflexive et dynamique.",
    content2: "Contribuer à l'analyse et à l'évolution des pratiques implique une capacité à se questionner, à confronter ses points de vue et à inscrire son action dans une démarche d'amélioration continue."
  },
  {
    id: 9, type: 'content',
    title: "La pratique réflexive : un marqueur de professionnalité",
    content: "L'analyse des pratiques constitue un outil fondamental de professionnalisation. Elle permet au professionnel de prendre du recul sur ses interventions, d'interroger ses représentations et d'améliorer la qualité de son accompagnement.",
    bullets: [
      "Questionner et ajuster ses pratiques régulièrement",
      "Organiser des temps d'analyse de pratiques en équipe",
      "Distinguer les faits des ressentis et des interprétations",
      "Accepter la confrontation des points de vue",
      "Identifier ses ressources et ses limites professionnelles",
      "La réflexivité protège contre la routine et l'usure"
    ]
  },
  {
    id: 10, type: 'content',
    title: "Les outils d'analyse",
    content: "L'analyse de pratiques peut s'appuyer sur différents dispositifs : groupes d'analyse, supervision, écrits réflexifs, observations croisées. Chaque dispositif offre un éclairage spécifique.",
    bullets: [
      "Groupes d'analyse de pratiques : espace protégé d'élaboration",
      "Supervision individuelle ou collective : regard extérieur",
      "Journal de bord professionnel : traçabilité réflexive",
      "Observations croisées entre collègues : regards complémentaires",
      "Lectures et formations : enrichissement théorique"
    ]
  },
  {
    id: 11, type: 'content',
    title: "Questionner et ajuster ses pratiques professionnelles",
    content: "Le questionnement professionnel représente le premier levier d'évolution. Il s'agit d'analyser ses interventions, d'identifier leurs effets et de repérer les éventuelles limites. Cette réflexion peut émerger à partir d'une situation complexe, d'un questionnement parental ou d'une difficulté rencontrée au sein du groupe.",
    content2: "Le professionnel adopte une posture d'auto-évaluation constructive. Il distingue les éléments relevant du cadre institutionnel, de ses choix pédagogiques ou de facteurs contextuels.",
    content3: "L'ajustement des pratiques découle de cette analyse. Il peut concerner la posture relationnelle, l'organisation des espaces, les modalités d'animation ou la communication avec les familles."
  },
  {
    id: 12, type: 'content',
    title: "Participer à l'organisation des espaces de régulation",
    content: "Les espaces de régulation permettent d'aborder les tensions, les difficultés et les ajustements nécessaires au sein de l'équipe. Ils contribuent à prévenir l'usure professionnelle et à maintenir un climat de travail serein.",
    content2: "Participer à leur organisation signifie reconnaître leur importance dans la qualité du service rendu. Ces espaces peuvent prendre la forme de réunions spécifiques, de temps d'échange encadrés ou d'interventions extérieures.",
    content3: "La régulation favorise la clarification des rôles, la résolution des conflits et la cohérence des pratiques."
  },
  {
    id: 13, type: 'content',
    title: "Organiser et participer à des temps d'échanges de pratiques",
    content: "Les échanges de pratiques constituent des moments privilégiés de partage d'expériences. Ils permettent de confronter les points de vue, de mutualiser les savoir-faire et de s'enrichir des expériences de chacun.",
    content2: "Ces temps peuvent être formalisés au sein de l'équipe ou ouverts à des professionnels d'autres structures. Ils favorisent une dynamique d'apprentissage collectif.",
    content3: "La participation active à ces échanges renforce la capacité d'adaptation et la créativité professionnelle."
  },
  {
    id: 14, type: 'content',
    title: "Organiser et participer à des temps d'analyse de pratiques",
    content: "L'analyse de pratiques représente une démarche structurée visant à approfondir la compréhension des situations professionnelles. Elle s'appuie sur la présentation d'une situation concrète et sur un travail collectif d'interprétation et de réflexion.",
    content2: "Encadrée par un professionnel formé à cette méthode, l'analyse de pratiques favorise la prise de distance et la compréhension des enjeux relationnels et institutionnels.",
    content3: "Elle contribue au développement de compétences réflexives et à l'amélioration continue des interventions."
  },
  {
    id: 15, type: 'content',
    title: "Synthèse",
    content: "Contribuer à l'analyse et à l'évolution des pratiques professionnelles demande engagement, ouverture et capacité de remise en question. En participant activement aux espaces de régulation, aux échanges et aux analyses de pratiques, le professionnel s'inscrit dans une dynamique collective d'amélioration. Cette posture réflexive renforce la qualité de l'accompagnement proposé aux jeunes enfants et à leurs familles, tout en consolidant la cohérence institutionnelle et partenariale."
  },
  // 4.2 S'INFORMER ET SE FORMER
  { id: 16, type: 'section', title: "4.2 – S'informer et se former pour faire évoluer ses pratiques" },
  {
    id: 17, type: 'content',
    title: "S'informer et se former pour faire évoluer ses pratiques",
    content: "L'évolution des pratiques professionnelles repose sur une démarche active de formation continue et de veille. Le champ de la petite enfance est traversé par des évolutions scientifiques, sociales et réglementaires constantes. Les connaissances en psychologie du développement, en pédagogie, en santé ou en inclusion se renouvellent régulièrement.",
    content2: "S'informer et se former constitue donc une responsabilité professionnelle. Cette dynamique contribue à maintenir la qualité des accompagnements proposés et à inscrire l'action éducative dans une approche actualisée et cohérente."
  },
  {
    id: 18, type: 'content',
    title: "La formation continue : un engagement professionnel",
    content: "S'informer et se former pour faire évoluer ses pratiques relève d'un engagement professionnel constant. Le secteur de la petite enfance est en évolution permanente : nouvelles connaissances, réformes législatives, innovations pédagogiques.",
    bullets: [
      "Se documenter et se tenir informé de l'actualité du secteur",
      "Actualiser ses connaissances théoriques et pratiques",
      "Participer à des colloques, séminaires et formations",
      "Contribuer à des travaux de recherche et de réflexion",
      "Mener des réflexions collectives au sein de l'équipe",
      "Partager ses connaissances avec les acteurs du territoire"
    ],
    note: "La formation continue prévient l'usure professionnelle et soutient l'innovation pédagogique."
  },
  {
    id: 19, type: 'content',
    title: "Se documenter et se tenir informé de l'actualité",
    content: "Le professionnel développe une veille régulière concernant les évolutions législatives, les recommandations institutionnelles et les recherches récentes. Cette veille peut s'appuyer sur des publications spécialisées, des rapports institutionnels, des conférences ou des ressources numériques fiables.",
    content2: "Se tenir informé permet d'anticiper les changements réglementaires et d'adapter les pratiques aux nouvelles orientations. Cela contribue également à enrichir les réflexions menées en équipe.",
    content3: "La capacité à identifier des sources pertinentes et fiables constitue une compétence essentielle dans un contexte d'abondance informationnelle."
  },
  {
    id: 20, type: 'content',
    title: "Actualiser ses connaissances dans le champ éducatif et pédagogique",
    content: "L'actualisation des connaissances concerne les théories du développement, les approches pédagogiques, les enjeux liés à la parentalité ou à l'inclusion. Participer à des formations, des colloques ou des séminaires permet d'approfondir ces thématiques.",
    content2: "L'enrichissement des connaissances théoriques nourrit la pratique quotidienne. Il favorise une meilleure compréhension des comportements observés et soutient la capacité d'argumentation professionnelle.",
    content3: "La formation continue contribue également à prévenir l'usure professionnelle en renouvelant les perspectives."
  },
  {
    id: 21, type: 'content',
    title: "Contribuer à des travaux de recherche et de réflexion",
    content: "Le professionnel peut s'impliquer dans des démarches de recherche-action ou dans des groupes de réflexion thématiques. Ces initiatives favorisent l'analyse approfondie des pratiques et la production de connaissances à partir du terrain.",
    content2: "La participation à des travaux de recherche permet de valoriser l'expertise des professionnels de terrain et d'inscrire les pratiques dans une dynamique scientifique.",
    content3: "Cette contribution renforce la professionnalisation du secteur et soutient l'innovation pédagogique."
  },
  {
    id: 22, type: 'content',
    title: "Mener des réflexions collectives sur la conduite de l'action éducative",
    content: "La formation ne se limite pas à l'acquisition individuelle de connaissances. Elle s'inscrit également dans une dynamique collective. Les échanges en équipe, les analyses de pratiques et les débats pédagogiques favorisent l'évolution des représentations et des méthodes.",
    content2: "Ces réflexions collectives permettent d'ajuster les actions éducatives et de maintenir une cohérence institutionnelle.",
    content3: "La confrontation des points de vue enrichit la compréhension des situations complexes."
  },
  {
    id: 23, type: 'content',
    title: "Partager ses connaissances avec les acteurs du territoire",
    content: "La transmission des savoirs constitue un prolongement de la formation. Le professionnel peut partager les connaissances acquises avec son équipe, les familles ou les partenaires territoriaux.",
    content2: "Cette diffusion peut prendre la forme d'interventions, de supports écrits ou de temps d'échange thématiques. Elle contribue à renforcer la cohérence éducative et à promouvoir une culture commune autour du développement du jeune enfant.",
    content3: "Le partage des connaissances favorise également la reconnaissance professionnelle."
  },
  {
    id: 24, type: 'content',
    title: "Rechercher et partager des informations, y compris en langue étrangère",
    content: "Dans un contexte d'ouverture internationale et de diversité culturelle, la capacité à consulter des ressources en langue étrangère constitue un atout. Elle permet d'accéder à des travaux de recherche et à des expériences innovantes.",
    content2: "Le partage de ces informations enrichit la réflexion collective et favorise l'ouverture à d'autres pratiques éducatives.",
    content3: "La maîtrise des outils numériques facilite cette recherche et cette diffusion d'informations."
  },
  {
    id: 25, type: 'content',
    title: "Synthèse",
    content: "S'informer et se former pour faire évoluer ses pratiques relève d'un engagement professionnel constant. En développant une veille active, en participant à des formations et en partageant ses connaissances, le professionnel contribue à la qualité et à l'actualisation de l'action éducative. Cette démarche dynamique soutient l'innovation, renforce la cohérence institutionnelle et participe à l'amélioration continue des pratiques au service du jeune enfant et de sa famille."
  },
  // 4.3 DÉCLINER LES ORIENTATIONS
  { id: 26, type: 'section', title: "4.3 – Décliner les orientations et les missions de la structure au travers du projet social, éducatif et pédagogique" },
  {
    id: 27, type: 'content',
    title: "Décliner les orientations et les missions de la structure",
    content: "Décliner les orientations et les missions d'une structure signifie transformer des principes généraux en actions concrètes et opérationnelles. Les orientations institutionnelles définissent une intention, une vision et des valeurs. Le projet social, éducatif et pédagogique en constitue la traduction structurée et contextualisée.",
    content2: "Le professionnel ne se contente pas d'appliquer un cadre prédéfini. Il participe activement à sa mise en œuvre, en veillant à ce que les pratiques quotidiennes reflètent les missions fondamentales de la structure."
  },
  {
    id: 28, type: 'content',
    title: "Du projet aux actions concrètes",
    content: "Décliner les orientations signifie transformer des principes généraux en actions concrètes et opérationnelles. Le projet social, éducatif et pédagogique est la traduction structurée et contextualisée des orientations institutionnelles.",
    bullets: [
      "Identifier les orientations et missions de la structure",
      "Traduire les orientations dans la rédaction d'un projet",
      "Définir des objectifs pédagogiques précis",
      "Formaliser les phases du projet (diagnostic, action, évaluation)",
      "Adapter le projet au territoire et au public accueilli"
    ]
  },
  {
    id: 29, type: 'content',
    title: "Identifier les orientations et les missions",
    content: "La première étape consiste à analyser les textes fondateurs de la structure : projet d'établissement, règlement intérieur, documents institutionnels ou conventions partenariales. Ces documents précisent les missions, les publics visés et les valeurs portées.",
    content2: "Identifier les orientations suppose également de comprendre les politiques publiques auxquelles la structure est rattachée. Les missions peuvent concerner l'accueil du jeune enfant, le soutien à la parentalité, l'inclusion sociale ou la prévention.",
    content3: "Cette analyse permet au professionnel de situer son action dans un cadre institutionnel cohérent."
  },
  {
    id: 30, type: 'content',
    title: "Traduire les orientations dans la rédaction d'un projet",
    content: "Les orientations éducatives prennent forme à travers la rédaction du projet social, éducatif et pédagogique. Cette rédaction nécessite clarté, structuration et cohérence.",
    content2: "Traduire une orientation en projet implique d'expliciter les valeurs sous-jacentes, de définir les axes prioritaires et de préciser les modalités d'intervention. Par exemple, une orientation centrée sur l'autonomie se traduira par des choix d'aménagement, des pratiques favorisant l'initiative et une posture éducative spécifique.",
    content3: "La rédaction rend le projet lisible pour les équipes, les familles et les partenaires."
  },
  {
    id: 31, type: 'content',
    title: "Définir des objectifs pédagogiques",
    content: "Les orientations générales doivent être déclinées en objectifs pédagogiques concrets et observables. Ces objectifs structurent l'action éducative et facilitent son évaluation.",
    content2: "Un objectif pédagogique précise ce que la structure souhaite favoriser chez l'enfant : développement de l'autonomie, renforcement des compétences sociales, accès à la culture ou inclusion.",
    content3: "La définition d'objectifs clairs permet d'orienter les pratiques quotidiennes et d'assurer une cohérence d'équipe."
  },
  {
    id: 32, type: 'content',
    title: "Formaliser les différentes phases du projet",
    content: "Un projet structuré comporte plusieurs phases : analyse des besoins, définition des objectifs, planification des actions, mise en œuvre et évaluation. Formaliser ces étapes garantit la rigueur méthodologique.",
    content2: "L'analyse des besoins s'appuie sur l'observation du public accueilli et sur les données territoriales. La planification précise les moyens humains, matériels et financiers mobilisés. L'évaluation permet de mesurer l'impact et d'ajuster les actions.",
    content3: "Cette formalisation favorise la continuité et la lisibilité du projet."
  },
  {
    id: 33, type: 'content',
    title: "Adapter le projet au territoire et au public",
    content: "Chaque structure s'inscrit dans un contexte territorial spécifique. Les caractéristiques sociales, culturelles et économiques influencent les besoins des familles et les priorités éducatives.",
    content2: "Adapter le projet signifie tenir compte de ces réalités. Un territoire rural, urbain ou prioritaire présente des enjeux différents. Le public accueilli peut également présenter des particularités en termes d'âge, de diversité culturelle ou de situations de vulnérabilité.",
    content3: "L'adaptation garantit la pertinence et l'efficacité du projet."
  },
  {
    id: 34, type: 'content',
    title: "Synthèse",
    content: "Décliner les orientations et les missions de la structure à travers le projet social, éducatif et pédagogique exige analyse, méthode et engagement. Le professionnel transforme des principes institutionnels en actions concrètes, adaptées au territoire et aux besoins des enfants et des familles. Cette démarche assure la cohérence entre les valeurs affichées et les pratiques quotidiennes, consolidant ainsi la qualité de l'accompagnement proposé."
  },
  // 4.4 REPRÉSENTER
  { id: 35, type: 'section', title: "4.4 – Représenter le service, l'établissement, l'institution" },
  {
    id: 36, type: 'content',
    title: "Porter les valeurs de la structure",
    content: "Représenter le service ou l'institution engage l'image, la crédibilité et la cohérence institutionnelle. Le professionnel devient un interlocuteur identifiable auprès des familles, des partenaires et des acteurs du territoire.",
    bullets: [
      "Se situer dans le projet et l'organisation de la structure",
      "Se présenter de manière claire et professionnelle",
      "Présenter les objectifs, les modalités et les valeurs de la structure",
      "Identifier la place de la structure dans son environnement",
      "Maintenir un discours cohérent avec les missions définies"
    ]
  },
  // 4.4 REPRÉSENTER (suite)
  {
    id: 37, type: 'content',
    title: "Représenter le service, l'établissement, l'institution",
    content: "Représenter le service, l'établissement ou l'institution constitue une responsabilité professionnelle majeure. Le professionnel devient un interlocuteur identifiable auprès des familles, des partenaires et des acteurs du territoire. Il incarne les valeurs, les orientations et les missions de la structure à travers ses paroles et ses actes.",
    content2: "Cette représentation ne se limite pas à une fonction formelle. Elle engage l'image, la crédibilité et la cohérence institutionnelle. Elle nécessite une connaissance précise du projet et une posture professionnelle adaptée."
  },
  {
    id: 38, type: 'content',
    title: "Se situer dans un service, un établissement, une institution et son projet",
    content: "Avant de représenter une structure, le professionnel doit comprendre son fonctionnement, ses missions et ses orientations. Il identifie les objectifs du projet social, éducatif et pédagogique, ainsi que les valeurs qui le fondent.",
    content2: "Se situer signifie également connaître son rôle, ses responsabilités et ses limites. Le professionnel agit dans le cadre de ses compétences tout en respectant l'organisation hiérarchique et les procédures internes.",
    content3: "Cette connaissance favorise une représentation fidèle et cohérente de l'institution."
  },
  {
    id: 39, type: 'content',
    title: "Se présenter et présenter son service, son établissement, son institution",
    content: "La capacité à se présenter de manière claire et professionnelle est essentielle. Le professionnel explique sa fonction, ses missions et son rôle au sein de la structure.",
    content2: "Présenter l'établissement suppose d'en exposer les objectifs, les modalités d'accueil et les valeurs éducatives. Cette présentation peut intervenir lors de réunions partenariales, de rencontres avec des familles ou d'événements territoriaux.",
    content3: "Un discours structuré et accessible renforce la lisibilité et la crédibilité de la structure. Il contribue à instaurer un climat de confiance avec les interlocuteurs."
  },
  {
    id: 40, type: 'content',
    title: "Identifier la place de la structure dans son environnement et sur le territoire",
    content: "La représentation institutionnelle implique une compréhension du positionnement territorial. La structure s'inscrit dans un réseau d'acteurs : services sociaux, établissements scolaires, structures culturelles ou médico-sociales.",
    content2: "Identifier cette place permet d'articuler les actions éducatives avec les dynamiques locales. Le professionnel connaît les partenaires, les ressources disponibles et les dispositifs existants.",
    content3: "Cette connaissance territoriale favorise la coopération et renforce l'ancrage institutionnel."
  },
  {
    id: 41, type: 'content',
    title: "Synthèse",
    content: "Représenter le service, l'établissement ou l'institution demande professionnalisme, clarté et engagement. En se situant précisément dans le projet institutionnel, en présentant la structure de manière cohérente et en comprenant son inscription territoriale, le professionnel contribue à renforcer la visibilité, la crédibilité et la qualité des partenariats. Cette posture participe pleinement à la dynamique interinstitutionnelle et au rayonnement du projet éducatif."
  },
  // 4.5 DYNAMIQUES INTERINSTITUTIONNELLES
  { id: 42, type: 'section', title: "4.5 – Favoriser les dynamiques interinstitutionnelles et partenariales" },
  {
    id: 43, type: 'content',
    title: "Le partenariat : au-delà de la collaboration ponctuelle",
    content: "Les dynamiques interinstitutionnelles et partenariales sont un levier essentiel pour enrichir l'action éducative. Aucune structure ne fonctionne en vase clos. Le partenariat s'inscrit dans une logique de réseau fondée sur la complémentarité.",
    bullets: [
      "Identifier les partenaires et leurs missions",
      "Communiquer sur les actions éducatives menées",
      "Contribuer aux instances de concertation",
      "Développer et mutualiser des outils de réseau",
      "Participer à l'animation de la dynamique de réseau",
      "Conduire des actions éducatives conjointes"
    ]
  },
  // 4.5 DYNAMIQUES INTERINSTITUTIONNELLES (suite)
  {
    id: 44, type: 'content',
    title: "Favoriser les dynamiques interinstitutionnelles et partenariales",
    content: "Les dynamiques interinstitutionnelles et partenariales constituent un levier essentiel pour enrichir l'action éducative. Aucune structure ne fonctionne en vase clos. L'accompagnement du jeune enfant et de sa famille s'inscrit dans un écosystème composé d'acteurs sociaux, sanitaires, éducatifs, culturels et institutionnels.",
    content2: "Favoriser ces dynamiques suppose une connaissance du territoire, une capacité de coordination et une posture d'ouverture. Le partenariat ne se limite pas à une collaboration ponctuelle ; il s'inscrit dans une logique de réseau, fondée sur la complémentarité des compétences et la recherche d'une cohérence globale."
  },
  {
    id: 45, type: 'content',
    title: "Repères : partenaires incontournables et leur rôle",
    content: "Sur le terrain, l'EJE travaille rarement « seul ». La qualité de l'accueil et de l'accompagnement se construit avec un réseau.",
    bullets: [
      "Mairie (service petite enfance) : information des familles, inscription, organisation territoriale des modes d'accueil, relais petite enfance, projets de quartier",
      "PMI (Protection Maternelle et Infantile) : avis et contrôles, recommandations, suivi de santé et de prévention, soutien à la parentalité",
      "Conseil départemental : Aide sociale à l'enfance (ASE), CRIP, recueil et traitement des informations préoccupantes, dispositifs de prévention",
      "CAF / MSA : partenaire financier et de politique familiale, soutien aux EAJE, prestations, actions parentalité",
      "ARS : enjeux de santé publique et de prévention",
      "Conseil régional : politiques de formation et d'emploi, financement et coordination de dispositifs",
      "Réseau médico-social : CAMSP, CMPP, CMP, orthophonistes, psychomotriciens, médecins, sages-femmes, MDPH",
      "Partenaires culturels et éducatifs : médiathèque, musée, conservatoire, associations, écoles, structures sportives"
    ]
  },
  {
    id: 46, type: 'content',
    title: "Identifier les partenaires pour l'élaboration de l'action éducative, culturelle et sociale",
    content: "La première étape consiste à repérer les acteurs présents sur le territoire. Il peut s'agir de services sociaux, de structures médico-sociales, d'établissements scolaires, d'associations culturelles, d'artistes, de bibliothèques ou de services de santé.",
    content2: "Identifier les partenaires implique d'analyser leurs missions, leurs compétences et leurs champs d'intervention. Cette connaissance permet d'orienter les familles vers les ressources adaptées et d'enrichir les projets éducatifs.",
    content3: "Le partenariat doit répondre à des besoins identifiés et s'inscrire dans une logique de complémentarité."
  },
  {
    id: 47, type: 'content',
    title: "Communiquer et informer les partenaires",
    content: "La communication constitue le fondement de toute collaboration. Informer les partenaires des actions menées par la structure favorise la visibilité et la cohérence territoriale.",
    content2: "Cette communication peut prendre la forme de réunions, de comptes rendus ou de supports écrits. Elle permet de partager les orientations éducatives, les projets en cours et les besoins repérés.",
    content3: "La transparence et la régularité des échanges renforcent la confiance et facilitent la coordination."
  },
  {
    id: 48, type: 'content',
    title: "Contribuer aux instances de concertation",
    content: "Les instances de concertation réunissent différents acteurs autour de problématiques communes. Participer à ces espaces permet de partager les analyses, de coordonner les actions et de développer une vision globale du territoire.",
    content2: "Le professionnel apporte son expertise issue du terrain et contribue à la réflexion collective. Cette participation renforce la cohérence des interventions et évite les actions isolées.",
    content3: "Les temps de concertation favorisent également la connaissance mutuelle des partenaires."
  },
  {
    id: 49, type: 'content',
    title: "Développer et mutualiser des outils de fonctionnement du réseau",
    content: "Un réseau efficace repose sur des outils partagés : annuaires de partenaires, protocoles de coopération, supports de communication communs ou calendriers d'actions.",
    content2: "La mutualisation de ces outils facilite la coordination et renforce l'efficacité des interventions. Elle permet également d'optimiser les ressources disponibles.",
    content3: "Le professionnel contribue à l'élaboration et à la diffusion de ces outils."
  },
  {
    id: 50, type: 'content',
    title: "Participer à l'animation et à la dynamique de réseau",
    content: "La dynamique de réseau nécessite un engagement actif. Participer à son animation peut signifier organiser des rencontres, proposer des projets communs ou initier des échanges thématiques.",
    content2: "L'animation de réseau favorise l'innovation et la coopération. Elle contribue à créer un climat de confiance et à renforcer les liens interinstitutionnels.",
    content3: "Cette dynamique repose sur la régularité des échanges et la reconnaissance mutuelle des compétences."
  },
  {
    id: 51, type: 'content',
    title: "Associer les partenaires à la conception, à la mise en œuvre et à l'évaluation",
    content: "Le partenariat devient pleinement opérationnel lorsque les partenaires sont impliqués dès la phase de conception des projets. Leur expertise enrichit l'analyse des besoins et la définition des objectifs.",
    content2: "La mise en œuvre conjointe permet de mutualiser les ressources et d'assurer une complémentarité des interventions. L'évaluation partagée offre un regard croisé sur les résultats obtenus et favorise l'ajustement des actions.",
    content3: "Cette démarche collaborative renforce la pertinence et la qualité des projets."
  },
  {
    id: 52, type: 'content',
    title: "Conduire des actions éducatives conjointes",
    content: "Les actions conjointes peuvent prendre différentes formes : projets culturels, ateliers de prévention, initiatives d'inclusion ou actions de soutien à la parentalité.",
    content2: "La coordination de ces actions exige organisation et clarté dans la répartition des rôles. Chaque partenaire doit connaître ses responsabilités et ses marges d'intervention.",
    content3: "Ces actions renforcent la cohérence territoriale et offrent aux enfants et aux familles des réponses globales et adaptées."
  },
  {
    id: 53, type: 'content',
    title: "Synthèse",
    content: "Favoriser les dynamiques interinstitutionnelles et partenariales demande ouverture, méthode et engagement. En identifiant les partenaires, en participant aux concertations et en conduisant des actions conjointes, le professionnel contribue à inscrire la structure dans un réseau cohérent et dynamique. Cette coopération enrichit l'action éducative et renforce la qualité de l'accompagnement proposé au jeune enfant et à sa famille dans leur environnement social et territorial."
  },
  // 4.6 ARTICULATION DES ACTIONS
  { id: 54, type: 'section', title: "4.6 – Contribuer à l'articulation des actions menées par les acteurs partenaires et les institutions ressources" },
  {
    id: 55, type: 'content',
    title: "Articuler les actions entre acteurs et institutions",
    content: "L'articulation entre les différents acteurs garantit la cohérence et la continuité de l'accompagnement. Le professionnel identifie les complémentarités et facilite la coordination des interventions.",
    bullets: [
      "Identifier les acteurs et institutions ressources du territoire",
      "Intégrer la dimension partenariale dans le projet éducatif",
      "Mobiliser les partenaires pour enrichir les projets",
      "Assurer la circulation des informations entre partenaires",
      "Coordonner les interventions dans le respect des compétences de chacun",
      "Formaliser les modalités de collaboration"
    ]
  },
  // 4.6 ARTICULATION DES ACTIONS (suite)
  {
    id: 56, type: 'content',
    title: "Contribuer à l'articulation des actions menées par les acteurs partenaires et les institutions ressources",
    content: "L'articulation des actions entre les différents partenaires constitue un enjeu central dans le champ de la petite enfance. Le jeune enfant et sa famille peuvent être accompagnés simultanément par plusieurs acteurs : structures d'accueil, services sociaux, professionnels de santé, institutions scolaires ou associations culturelles.",
    content2: "Contribuer à cette articulation signifie favoriser la cohérence, éviter les ruptures de parcours et assurer une complémentarité des interventions. Le professionnel agit comme un médiateur et un coordinateur, garantissant la fluidité des échanges et la cohérence des projets."
  },
  {
    id: 57, type: 'content',
    title: "Identifier les acteurs, les personnes et les institutions ressources",
    content: "La première étape consiste à repérer les ressources disponibles sur le territoire. Il peut s'agir de services de protection maternelle et infantile, de centres médico-sociaux, d'établissements scolaires, de structures culturelles ou d'associations spécialisées.",
    content2: "Identifier ces acteurs suppose de connaître leurs missions, leurs modalités d'intervention et leurs champs de compétence. Cette connaissance permet d'orienter les familles vers les services adaptés et d'enrichir les projets éducatifs.",
    content3: "Le professionnel tient à jour une cartographie des ressources territoriales afin de faciliter les collaborations."
  },
  {
    id: 58, type: 'content',
    title: "Intégrer la dimension partenariale dans la conception du projet",
    content: "La dimension partenariale doit être envisagée dès la conception du projet éducatif et social. Elle ne constitue pas un ajout secondaire, mais un élément structurant.",
    content2: "Intégrer cette dimension signifie anticiper les collaborations possibles, définir les rôles des partenaires et préciser les modalités de coordination. Cette anticipation favorise la cohérence et l'efficacité des actions menées.",
    content3: "La réflexion partenariale permet également d'élargir les perspectives et d'enrichir les propositions éducatives."
  },
  {
    id: 59, type: 'content',
    title: "Mobiliser les partenaires pour contribuer aux projets",
    content: "La mobilisation des partenaires repose sur une communication claire et sur la reconnaissance de leurs compétences. Le professionnel présente les objectifs du projet, précise les attentes et définit les modalités de participation.",
    content2: "Cette mobilisation nécessite organisation et coordination. Les rôles doivent être explicités afin d'éviter les ambiguïtés.",
    content3: "La collaboration s'inscrit dans une logique de complémentarité et de respect des champs d'intervention de chacun."
  },
  {
    id: 60, type: 'content',
    title: "Identifier les procédures et protocoles de communication",
    content: "Chaque institution possède ses propres procédures et règles de communication. Les respecter constitue une condition essentielle de la coopération.",
    content2: "Le professionnel veille à connaître les modalités de transmission d'informations, les règles de confidentialité et les protocoles d'intervention. Cette connaissance garantit la sécurité juridique et la qualité des échanges.",
    content3: "Le respect des procédures renforce la confiance entre partenaires."
  },
  {
    id: 61, type: 'content',
    title: "Identifier, ordonnancer et hiérarchiser les informations",
    content: "La coordination interinstitutionnelle nécessite une transmission d'informations claire et structurée. Le professionnel sélectionne les données pertinentes et les organise de manière logique.",
    content2: "La hiérarchisation des informations évite la surcharge et facilite la compréhension. Les éléments essentiels doivent être mis en avant, tandis que les détails secondaires sont contextualisés.",
    content3: "Cette rigueur contribue à l'efficacité des échanges et à la continuité des actions."
  },
  {
    id: 62, type: 'content',
    title: "Valoriser le projet éducatif et social dans les relations partenariales",
    content: "Dans ses échanges avec les partenaires, le professionnel met en avant les orientations et les valeurs portées par la structure. Il présente les projets en cours et les résultats obtenus.",
    content2: "Cette valorisation renforce la visibilité de la structure et consolide son positionnement institutionnel. Elle favorise également l'adhésion des partenaires aux projets proposés.",
    content3: "La communication institutionnelle doit rester cohérente et fidèle aux missions définies."
  },
  {
    id: 63, type: 'content',
    title: "Proposer des pistes de réflexion dans les projets territoriaux",
    content: "Le professionnel peut contribuer à l'élaboration de projets territoriaux de politique sociale concernant la petite enfance. Il apporte son expertise issue du terrain et formule des propositions argumentées.",
    content2: "Ces contributions peuvent concerner l'amélioration de l'offre d'accueil, la prévention, l'inclusion ou le soutien à la parentalité.",
    content3: "Argumenter ses propositions suppose une analyse rigoureuse des besoins du territoire et une capacité à formuler des recommandations structurées."
  },
  {
    id: 64, type: 'content',
    title: "Synthèse",
    content: "Contribuer à l'articulation des actions menées par les partenaires et les institutions ressources exige méthode, communication et vision globale. En identifiant les acteurs, en intégrant la dimension partenariale dans les projets et en structurant les échanges, le professionnel garantit une cohérence territoriale au service du jeune enfant et de sa famille. Cette dynamique renforce la qualité de l'accompagnement et consolide l'inscription institutionnelle de la structure dans son environnement."
  },
  // 4.7 VEILLE SUR LES POLITIQUES
  { id: 65, type: 'section', title: "4.7 – Développer une veille sur les évolutions des politiques liées au secteur de la petite enfance" },
  {
    id: 66, type: 'content',
    title: "Développer une veille active",
    content: "La veille ne se limite pas à l'observation. Elle conduit à proposer des ajustements ou des innovations au sein de la structure. Les évolutions politiques peuvent nécessiter une adaptation des projets ou des modalités d'accueil.",
    bullets: [
      "Développer une veille active sur les politiques petite enfance",
      "Participer à des séminaires et formations spécialisées",
      "S'inscrire dans les réseaux professionnels et institutionnels",
      "Savoir remonter les besoins du terrain aux instances décisionnaires",
      "Proposer des pistes argumentées d'évolution des pratiques",
      "Utiliser des outils de veille et de partage d'informations"
    ]
  },
  // 4.7 VEILLE SUR LES POLITIQUES (suite)
  {
    id: 67, type: 'content',
    title: "Développer une veille sur les évolutions des politiques liées au secteur de la petite enfance",
    content: "Le secteur de la petite enfance est étroitement lié aux orientations des politiques publiques. Les évolutions législatives, réglementaires et institutionnelles influencent directement les missions, les modalités d'accueil et les pratiques professionnelles. Développer une veille active permet d'anticiper ces changements, d'adapter les projets et de maintenir la qualité des accompagnements.",
    content2: "La veille professionnelle ne relève pas d'une démarche ponctuelle. Elle s'inscrit dans une dynamique continue d'actualisation des connaissances et de réflexion collective."
  },
  {
    id: 68, type: 'content',
    title: "Participer aux séminaires et aux formations",
    content: "La participation à des séminaires, colloques et formations permet de mieux comprendre les orientations nationales et territoriales en matière de petite enfance. Ces espaces offrent une lecture actualisée des enjeux éducatifs, sociaux et sanitaires.",
    content2: "Ils favorisent également la rencontre avec d'autres professionnels et l'échange de pratiques. Cette ouverture enrichit la compréhension des évolutions institutionnelles et soutient l'innovation.",
    content3: "La formation continue constitue un levier essentiel d'adaptation aux nouvelles exigences réglementaires."
  },
  {
    id: 69, type: 'content',
    title: "Participer à des groupes de réflexion et d'analyse des pratiques",
    content: "Les groupes de réflexion permettent d'analyser les impacts concrets des politiques publiques sur le terrain. Ils offrent un espace pour questionner les évolutions, partager les difficultés rencontrées et proposer des ajustements.",
    content2: "Ces temps collectifs favorisent la prise de distance et l'élaboration de réponses adaptées aux réalités professionnelles.",
    content3: "L'analyse des pratiques contribue à relier les orientations politiques aux interventions quotidiennes."
  },
  {
    id: 70, type: 'content',
    title: "Identifier et utiliser des outils de veille",
    content: "Les outils de veille peuvent inclure des abonnements à des revues spécialisées, des lettres d'information institutionnelles ou des plateformes numériques dédiées à la petite enfance. Les publications officielles des ministères, des collectivités territoriales ou des organismes de référence constituent des sources fiables.",
    content2: "L'utilisation d'outils numériques facilite la collecte et l'organisation des informations. La sélection rigoureuse des sources garantit la qualité et la pertinence des données recueillies.",
    content3: "La veille permet d'anticiper les évolutions réglementaires et d'adapter les projets institutionnels."
  },
  {
    id: 71, type: 'content',
    title: "Partager les informations au sein de la structure",
    content: "La veille ne prend tout son sens que si les informations sont diffusées. Le professionnel partage les éléments pertinents avec l'équipe lors de réunions ou à travers des supports écrits.",
    content2: "Cette transmission favorise une compréhension commune des évolutions politiques et soutient la cohérence des pratiques.",
    content3: "Le partage d'informations contribue également à renforcer la culture professionnelle de la structure."
  },
  {
    id: 72, type: 'content',
    title: "Utiliser des outils de partage internes",
    content: "Les outils numériques internes, tels que les espaces collaboratifs ou les dossiers partagés, facilitent la diffusion et l'archivage des informations. Ils permettent à l'équipe d'accéder aux documents de référence et de suivre les évolutions réglementaires.",
    content2: "La structuration de ces outils renforce l'efficacité et la lisibilité des données partagées."
  },
  {
    id: 73, type: 'content',
    title: "S'inscrire dans les réseaux institutionnels et professionnels",
    content: "La participation aux réseaux professionnels et institutionnels favorise l'accès à des informations actualisées et à des échanges d'expériences. Ces réseaux participent à la production et à la diffusion de connaissances sur l'action éducative en direction des jeunes enfants.",
    content2: "S'inscrire dans ces dynamiques renforce l'ancrage territorial de la structure et favorise la coopération interinstitutionnelle."
  },
  {
    id: 74, type: 'content',
    title: "Proposer et développer des évolutions des pratiques",
    content: "La veille ne se limite pas à l'observation. Elle peut conduire à proposer des ajustements ou des innovations au sein de la structure. Les évolutions politiques peuvent nécessiter une adaptation des projets pédagogiques ou des modalités d'accueil.",
    content2: "Le professionnel formule des propositions argumentées en s'appuyant sur les informations recueillies et sur l'analyse des besoins du terrain.",
    content3: "Cette démarche contribue à maintenir une pratique dynamique et actualisée."
  },
  {
    id: 75, type: 'content',
    title: "Rechercher et partager, y compris en langue étrangère",
    content: "L'ouverture à des ressources internationales permet d'élargir la perspective et de découvrir d'autres modèles d'intervention. La consultation de publications en langue étrangère enrichit la réflexion et favorise l'innovation.",
    content2: "Le partage de ces informations au sein de l'équipe contribue à une ouverture culturelle et professionnelle."
  },
  {
    id: 76, type: 'content',
    title: "Synthèse",
    content: "Développer une veille sur les évolutions des politiques liées au secteur de la petite enfance exige curiosité, rigueur et engagement. En participant à des formations, en utilisant des outils de veille et en partageant les informations collectées, le professionnel contribue à adapter les pratiques aux évolutions institutionnelles. Cette dynamique soutient la qualité, la pertinence et la cohérence de l'action éducative au service du jeune enfant et de sa famille."
  },
  // 4.8 PROJETS DE DÉVELOPPEMENT
  { id: 77, type: 'section', title: "4.8 – Contribuer à des projets de développement de modes d'accueil" },
  {
    id: 78, type: 'content',
    title: "Contribuer au développement des modes d'accueil",
    content: "Le professionnel participe à l'évolution de l'offre d'accueil sur le territoire en contribuant à des projets de développement ou d'adaptation des structures existantes.",
    bullets: [
      "Analyser les besoins d'accueil sur le territoire",
      "Participer à l'élaboration de projets de création ou d'extension",
      "Contribuer aux diagnostics territoriaux",
      "Proposer des innovations en matière d'accueil",
      "S'inscrire dans les démarches de schéma départemental",
      "Collaborer avec les partenaires institutionnels et financeurs"
    ]
  },
  // 4.8 PROJETS DE DÉVELOPPEMENT (suite)
  {
    id: 79, type: 'content',
    title: "Contribuer à des projets de développement de modes d'accueil",
    content: "Le développement des modes d'accueil constitue un enjeu majeur des politiques publiques de la petite enfance. L'évolution des besoins des familles, les transformations sociales et les dynamiques territoriales nécessitent une adaptation constante de l'offre d'accueil.",
    content2: "Le professionnel peut contribuer activement à ces projets en apportant son expertise de terrain, sa connaissance des besoins des familles et son analyse des réalités locales. Cette implication participe à l'amélioration de l'accessibilité, de la qualité et de la pertinence des dispositifs d'accueil."
  },
  {
    id: 80, type: 'content',
    title: "Participer au repérage et à l'identification des besoins sur un territoire",
    content: "L'analyse des besoins constitue la première étape de tout projet de développement. Elle repose sur l'observation des situations rencontrées, les échanges avec les familles et la connaissance des données territoriales.",
    content2: "Le professionnel identifie les attentes en matière de places d'accueil, d'horaires élargis, d'inclusion d'enfants en situation de handicap ou de soutien à la parentalité. Il prend en compte les caractéristiques socio-économiques, culturelles et démographiques du territoire.",
    content3: "Cette démarche analytique permet d'objectiver les besoins et de formuler des propositions adaptées."
  },
  {
    id: 81, type: 'content',
    title: "Savoir remonter les besoins aux instances décisionnaires",
    content: "La transmission des besoins identifiés constitue une responsabilité professionnelle. Le professionnel participe à la circulation de l'information vers les responsables institutionnels, les collectivités territoriales ou les instances de concertation.",
    content2: "Cette remontée d'informations doit être structurée et argumentée. Elle s'appuie sur des données observables, des analyses et des exemples concrets issus du terrain.",
    content3: "La capacité à formaliser ces éléments renforce la crédibilité des propositions et favorise leur prise en compte par les décideurs."
  },
  {
    id: 82, type: 'content',
    title: "Contribuer à une réflexion sur l'adaptation des modes d'accueil",
    content: "Les modes d'accueil doivent évoluer en fonction des transformations sociales et des besoins spécifiques des familles. Le professionnel participe à une réflexion collective sur l'adaptation des dispositifs existants ou la création de nouvelles formes d'accueil.",
    content2: "Cette réflexion peut porter sur la flexibilité des horaires, la diversification des offres, l'accueil inclusif ou la mise en place de projets innovants. Elle nécessite une analyse des ressources disponibles et des contraintes institutionnelles.",
    content3: "L'objectif est de proposer des réponses adaptées, accessibles et cohérentes avec les missions sociales de la structure."
  },
  {
    id: 83, type: 'content',
    title: "Participer à la conduite de projets partenariaux sur le territoire",
    content: "Le développement des modes d'accueil s'inscrit souvent dans une dynamique partenariale. Les collectivités territoriales, les services sociaux, les associations et les institutions éducatives peuvent être impliqués dans ces projets.",
    content2: "Le professionnel contribue à la coordination des actions, à la participation aux réunions de travail et à la mise en œuvre des décisions prises. Il veille à articuler les différents acteurs et à maintenir une cohérence d'ensemble.",
    content3: "Cette participation renforce l'ancrage territorial de la structure et favorise une réponse globale aux besoins des familles."
  },
  {
    id: 84, type: 'content',
    title: "Synthèse",
    content: "Contribuer à des projets de développement de modes d'accueil implique engagement, analyse et coopération. En identifiant les besoins, en les transmettant aux instances compétentes et en participant aux dynamiques partenariales, le professionnel participe activement à l'évolution de l'offre d'accueil. Cette implication renforce la pertinence des dispositifs et soutient l'adaptation continue des services aux réalités du territoire et aux attentes des familles."
  },
  // 4.9 GROUPES DE TRAVAIL
  { id: 85, type: 'section', title: "4.9 – Conduire des groupes de travail sur des thématiques liées à la petite enfance" },
  {
    id: 86, type: 'content',
    title: "Conduire des groupes de travail thématiques",
    content: "La conduite de groupes de travail mobilise des compétences d'animation, de coordination et de synthèse. Ces groupes permettent de traiter collectivement des thématiques spécifiques liées à la petite enfance.",
    bullets: [
      "Définir les objectifs et les modalités du groupe de travail",
      "Identifier et mobiliser les participants pertinents",
      "Animer les échanges et favoriser la participation",
      "Produire des synthèses et des recommandations",
      "Assurer le suivi et la mise en œuvre des préconisations",
      "Rendre compte des travaux aux instances concernées"
    ]
  },
  // 4.9 GROUPES DE TRAVAIL (suite)
  {
    id: 87, type: 'content',
    title: "Conduire des groupes de travail sur des thématiques liées à la petite enfance",
    content: "La conduite de groupes de travail constitue un levier important de professionnalisation et d'évolution des pratiques. Elle permet d'approfondir des thématiques spécifiques, de partager des expériences et de construire collectivement des réponses adaptées aux enjeux rencontrés sur le terrain.",
    content2: "Conduire un groupe de travail demande des compétences organisationnelles, pédagogiques et relationnelles. Le professionnel adopte une posture d'animateur et de facilitateur, favorisant l'expression et la participation active des membres."
  },
  {
    id: 88, type: 'content',
    title: "Repérer et identifier les besoins de connaissances et de réflexion",
    content: "La mise en place d'un groupe de travail repose sur l'identification de besoins réels. Ces besoins peuvent émerger de situations professionnelles complexes, d'évolutions réglementaires ou de questionnements partagés au sein de l'équipe.",
    content2: "Le professionnel analyse les problématiques rencontrées et identifie les axes nécessitant approfondissement. Cette analyse permet de cibler des thématiques pertinentes et en lien avec les préoccupations du terrain.",
    content3: "L'identification des besoins garantit la légitimité et l'utilité du groupe de travail."
  },
  {
    id: 89, type: 'content',
    title: "Identifier et choisir les thèmes de travail",
    content: "Le choix des thématiques doit être cohérent avec les orientations institutionnelles et les attentes des professionnels. Les sujets peuvent concerner le développement de l'enfant, l'inclusion, la parentalité, la prévention ou l'aménagement des espaces.",
    content2: "La définition claire du thème permet de structurer les échanges et de fixer des objectifs précis. Elle facilite également la préparation des supports et des ressources nécessaires.",
    content3: "Un thème bien délimité favorise une réflexion approfondie et constructive."
  },
  {
    id: 90, type: 'content',
    title: "Contribuer à la formation des professionnels",
    content: "Le groupe de travail peut constituer un espace de formation interne. Il favorise l'acquisition de nouvelles connaissances et le développement de compétences professionnelles.",
    content2: "Le professionnel peut partager des ressources théoriques, proposer des études de cas ou inviter des intervenants extérieurs. Cette démarche contribue à l'enrichissement collectif.",
    content3: "La formation par l'échange renforce la cohérence des pratiques et la dynamique d'équipe."
  },
  {
    id: 91, type: 'content',
    title: "Organiser des journées pédagogiques",
    content: "Les journées pédagogiques représentent des temps privilégiés pour approfondir une thématique et renforcer la cohésion professionnelle. Leur organisation nécessite anticipation et méthodologie.",
    content2: "Le professionnel définit les objectifs, planifie les interventions et prévoit des temps d'échanges participatifs. L'évaluation en fin de journée permet d'identifier les acquis et les pistes d'amélioration.",
    content3: "Ces journées contribuent à la construction d'une culture professionnelle commune."
  },
  {
    id: 92, type: 'content',
    title: "Organiser un groupe de travail structuré",
    content: "La conduite d'un groupe de travail suppose une organisation claire : définition des objectifs, planification des séances, choix des méthodes et formalisation des productions.",
    content2: "Le professionnel veille à établir un cadre respectueux favorisant la parole de chacun. Il assure la synthèse des échanges et formalise les conclusions ou recommandations issues des travaux.",
    content3: "Cette structuration garantit l'efficacité et la continuité du groupe."
  },
  {
    id: 93, type: 'content',
    title: "Utiliser les techniques participatives d'animation",
    content: "Les techniques participatives favorisent l'implication active des participants. Elles peuvent inclure des travaux en sous-groupes, des études de situations, des débats ou des outils collaboratifs.",
    content2: "L'animateur régule les échanges, encourage la participation et veille au respect du cadre. La reformulation et la synthèse régulière permettent de maintenir la cohérence des discussions.",
    content3: "L'utilisation de méthodes participatives renforce l'appropriation des contenus et la dynamique collective."
  },
  {
    id: 94, type: 'content',
    title: "Synthèse",
    content: "Conduire des groupes de travail sur des thématiques liées à la petite enfance nécessite méthode, écoute et capacité d'animation. En identifiant les besoins, en structurant les échanges et en mobilisant des techniques participatives, le professionnel contribue à la formation continue et à l'évolution des pratiques. Cette démarche collective renforce la qualité de l'accompagnement proposé aux jeunes enfants et à leurs familles, tout en consolidant la dynamique institutionnelle et territoriale."
  },
  // PARTENAIRES INCONTOURNABLES
  { id: 95, type: 'section', title: "Partenaires incontournables" },
  {
    id: 96, type: 'partners',
    title: "Les partenaires clés de l'EJE",
    partners: [
      { name: "Mairie", role: "Service petite enfance, inscriptions, projets de quartier" },
      { name: "PMI", role: "Avis, contrôles, suivi de santé, prévention, soutien parentalité" },
      { name: "Conseil départemental", role: "ASE, CRIP, informations préoccupantes, prévention" },
      { name: "CAF / MSA", role: "Financement EAJE, politique familiale, actions parentalité" },
      { name: "ARS", role: "Santé publique, prévention, enjeux sanitaires" },
      { name: "CAMSP / CMPP / CMP", role: "Accompagnement médico-social, inclusion" },
      { name: "MDPH", role: "Reconnaissance handicap, droits, accompagnement" },
      { name: "Partenaires culturels", role: "Médiathèque, musée, conservatoire, associations" }
    ]
  },
  // SYNTHÈSE
  {
    id: 97, type: 'summary',
    title: "Synthèse DC4",
    points: [
      "L'analyse des pratiques est un marqueur de professionnalité",
      "La formation continue prévient l'usure et soutient l'innovation",
      "Le projet est la traduction concrète des orientations",
      "Représenter la structure engage la cohérence institutionnelle",
      "Le partenariat enrichit l'action éducative",
      "La veille permet d'anticiper et d'adapter les projets"
    ],
    authors: "CASF, Convention droits de l'enfant, Charte nationale accueil, COG État-CAF"
  },
  // FÉLICITATIONS
  {
    id: 98, type: 'congrats',
    title: "Félicitations !",
    message: "Vous avez terminé le cours DC4 – Dynamiques interinstitutionnelles, partenariats et réseaux. Vous maîtrisez désormais les fondamentaux de l'analyse des pratiques, de la veille institutionnelle, du partenariat et de la conduite de projets. Passez maintenant au quiz pour valider vos connaissances !",
    cta: "Rendez-vous dans la section Quiz DC4 pour tester vos acquis"
  },
];

const DEEJEDC4Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewedSlides, setViewedSlides] = useState(new Set([0]));
  const audioRef = useRef(null);
  const API_URL = window.location.origin || '';

  useEffect(() => {
    const saved = getActivityProgress(ACTIVITY_ID);
    if (saved && saved.lastViewedSlide > 0) {
      setCurrentSlide(saved.lastViewedSlide);
      setViewedSlides(new Set(saved.viewedSlides || [0]));
      toast.info(`Reprise à la slide ${saved.lastViewedSlide + 1} / ${slides.length}`);
    }
    markSlideViewed(ACTIVITY_ID, 0, slides.length);
  }, []);

  const transformTextForTTS = (text) => {
    if (!text) return '';
    return text
      .replace(/\bDEEJE\b/g, "diplome d'etat d'educateur de jeunes enfants")
      .replace(/\bEJE\b/g, "educateur de jeunes enfants")
      .replace(/\bDC4\b/g, "domaine de competences 4")
      .replace(/\bPMI\b/g, "protection maternelle et infantile")
      .replace(/\bCAF\b/g, "caisse d'allocations familiales")
      .replace(/\bMSA\b/g, "mutualite sociale agricole")
      .replace(/\bARS\b/g, "agence regionale de sante")
      .replace(/\bASE\b/g, "aide sociale a l'enfance")
      .replace(/\bCRIP\b/g, "cellule de recueil des informations preoccupantes")
      .replace(/\bCAMSP\b/g, "centre d'action medico-sociale precoce")
      .replace(/\bCMPP\b/g, "centre medico-psycho-pedagogique")
      .replace(/\bCMP\b/g, "centre medico-psychologique")
      .replace(/\bMDPH\b/g, "maison departementale des personnes handicapees")
      .replace(/\bEAJE\b/g, "etablissement d'accueil du jeune enfant")
      .replace(/\bCASF\b/g, "code de l'action sociale et des familles")
      .replace(/\bCOG\b/g, "convention d'objectifs et de gestion")
      .replace(/\bVAE\b/g, "validation des acquis de l'experience");
  };

  const speakText = async (text) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const transformedText = transformTextForTTS(text).substring(0, 800);
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: transformedText, voice: 'nova', speed: 1.1 })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.audio_url || data.audio_base64) {
          if (audioRef.current) audioRef.current.pause();
          audioRef.current = new Audio(data.audio_url || `data:audio/mp3;base64,${data.audio_base64}`);
          audioRef.current.play();
          setIsPlaying(true);
          audioRef.current.onended = () => setIsPlaying(false);
        }
      }
    } catch (error) {
      toast.error("Erreur de connexion au service audio");
    } finally {
      setIsLoading(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; setIsPlaying(false); }
  };

  const getSlideText = (slide) => {
    let text = slide.title || '';
    if (slide.subtitle) text += '. ' + slide.subtitle;
    if (slide.content) text += '. ' + slide.content;
    if (slide.content2) text += '. ' + slide.content2;
    if (slide.content3) text += '. ' + slide.content3;
    if (slide.bullets) text += '. ' + slide.bullets.join('. ');
    if (slide.note) text += '. ' + slide.note;
    if (slide.partners) text += '. ' + slide.partners.map(p => p.name + ': ' + p.role).join('. ');
    return text;
  };

  const nextSlide = () => { if (currentSlide < slides.length - 1) { const next = currentSlide + 1; setCurrentSlide(next); setViewedSlides(prev => new Set([...prev, next])); markSlideViewed(ACTIVITY_ID, next, slides.length); } };
  const prevSlide = () => { if (currentSlide > 0) setCurrentSlide(currentSlide - 1); };
  const goToSlide = (index) => { setCurrentSlide(index); setViewedSlides(prev => new Set([...prev, index])); markSlideViewed(ACTIVITY_ID, index, slides.length); };

  const progressPercent = Math.round((viewedSlides.size / slides.length) * 100);

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', minHeight: '430px' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(45,106,79,0.60)' }} />
            <div className="relative z-10 flex flex-col items-center justify-center px-8">
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 drop-shadow-lg">{slide.title}</h1>
              <div className="space-y-2">
                <p className="text-lg md:text-xl font-medium drop-shadow" style={{ color: 'rgba(255,255,255,0.70)' }}>{slide.bloc7}</p>
                <p className="text-lg md:text-xl font-medium drop-shadow" style={{ color: 'rgba(255,255,255,0.70)' }}>{slide.bloc8}</p>
              </div>
            </div>
          </div>
        );
      case 'sommaire':
        return (
          <div className="h-full flex flex-col relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(245,251,247,0.80)' }} />
            <div className="relative z-10 h-full flex flex-col">
              <div style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, #1B4332 100%)`, padding: '0.5rem 0' }}>
                <h2 className="text-xl font-bold text-center text-white tracking-widest uppercase">Sommaire</h2>
              </div>
              <div className="flex-1 overflow-auto px-3 py-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-w-5xl mx-auto">
                  {slide.sections.map((s, idx) => (
                    <div key={idx}
                      className="flex items-start gap-2 p-2 bg-white/90 rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.01] transition-all backdrop-blur-sm"
                      style={{ border: `1px solid ${theme.primary}20` }}
                      onClick={() => goToSlide(Math.min(idx * 2 + 2, slides.length - 1))}>
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white mt-0.5" style={{ background: theme.primary }}>{s.num}</span>
                      <span className="text-gray-700 text-xs leading-tight font-medium">{s.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'section':
        return (
          <div className="flex flex-col items-center justify-center relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', minHeight: '430px' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(27,67,50,0.65)' }} />
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center px-10 relative z-10 drop-shadow-lg whitespace-pre-line">{slide.title}</h2>
          </div>
        );
      case 'content':
        return (
          <div className="p-6 overflow-auto flex flex-col items-center justify-center" style={{ background: theme.bg, minHeight: '430px' }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <p className="text-gray-700 mb-3 leading-relaxed text-base text-center">{slide.content}</p>
              {slide.content2 && <p className="text-gray-700 mb-3 leading-relaxed text-base text-center">{slide.content2}</p>}
              {slide.content3 && <p className="text-gray-700 mb-3 leading-relaxed text-base text-center">{slide.content3}</p>}
              {slide.bullets && (
                <div className="space-y-2 mb-3">
                  {slide.bullets.map((b, i) => (
                    <div key={i} className="flex items-start gap-2 bg-white p-2.5 rounded-lg shadow-sm">
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: theme.primary }} />
                      <span className="text-gray-700 text-base">{b}</span>
                    </div>
                  ))}
                </div>
              )}
              {slide.note && (
                <div className="p-3 rounded-lg text-base italic text-center" style={{ background: theme.primary + '15', borderLeft: `3px solid ${theme.primary}` }}>
                  {slide.note}
                </div>
              )}
            </div>
          </div>
        );
      case 'competence':
        return (
          <div className="p-6 overflow-auto flex flex-col items-center justify-center" style={{ background: theme.bg, minHeight: '430px' }}>
            <div className="max-w-3xl mx-auto w-full">
              <div className="p-3 rounded-t-lg text-white font-bold text-center text-lg" style={{ background: theme.primary }}>
                {slide.title}
              </div>
              <div className="bg-white rounded-b-lg shadow-md p-4 space-y-2">
                {slide.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 p-2.5 border-b last:border-0" style={{ borderColor: theme.primary + '20' }}>
                    <Sparkles size={16} className="mt-0.5 flex-shrink-0" style={{ color: theme.secondary }} />
                    <span className="text-gray-700 text-base">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'partners':
        return (
          <div className="p-6 overflow-auto flex flex-col items-center justify-center" style={{ background: theme.bg, minHeight: '430px' }}>
            <div className="max-w-3xl mx-auto w-full">
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {slide.partners.map((p, i) => (
                  <div key={i} className="bg-white p-3 rounded-lg shadow-sm border-l-4" style={{ borderColor: i % 2 === 0 ? theme.primary : theme.secondary }}>
                    <h4 className="font-bold text-sm mb-1" style={{ color: theme.primary }}>{p.name}</h4>
                    <p className="text-gray-600 text-xs">{p.role}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'summary':
        return (
          <div className="flex flex-col items-center justify-center p-8 relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', minHeight: '430px' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(45,106,79,0.60)' }} />
            <div className="relative z-10 flex flex-col items-center">
              <h2 className="text-2xl font-bold text-white mb-4 text-center drop-shadow-lg">{slide.title}</h2>
              <div className="space-y-2 max-w-xl w-full mb-4">
                {slide.points.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.15)' }}>
                    <Handshake size={16} className="text-white flex-shrink-0" />
                    <span className="text-white text-base">{p}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 rounded-lg max-w-xl w-full" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <p className="text-sm text-center" style={{ color: theme.primary }}>
                  <strong>Références :</strong> {slide.authors}
                </p>
              </div>
            </div>
          </div>
        );
      case 'congrats':
        return (
          <div className="flex flex-col items-center justify-center relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', minHeight: '430px' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(45,106,79,0.65)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8">
              <div className="text-6xl mb-4">&#127891;</div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow-lg">{slide.title}</h2>
              <p className="text-white text-lg leading-relaxed max-w-2xl mb-6">{slide.message}</p>
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <p className="text-lg font-semibold" style={{ color: theme.primary }}>{slide.cta}</p>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  const slide = slides[currentSlide];
  return (
    <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: theme.bg }} data-testid="deeje-dc4-presentation">
      <div>{renderSlide(slide)}</div>
      <div className="w-full h-2 bg-gray-200">
        <div className="h-full rounded-r-full transition-all duration-300" style={{ width: `${progressPercent}%`, background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` }} />
      </div>
      <div className="flex items-center justify-between p-3 bg-white border-t" style={{ borderColor: theme.primary + '30' }}>
        <button onClick={prevSlide} disabled={currentSlide === 0} className="flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40" style={{ borderColor: theme.primary, color: theme.text }} data-testid="dc4-prev-btn">
          <ChevronLeft size={16} /> Précédent
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))} className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: theme.primary }} data-testid="dc4-audio-btn">
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : isPlaying ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span className="font-bold text-sm" style={{ color: theme.primary }}>{currentSlide + 1} / {slides.length}</span>
          <span className="text-xs text-gray-400">{progressPercent}%</span>
        </div>
        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40" style={{ background: theme.primary }} data-testid="dc4-next-btn">
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default DEEJEDC4Presentation;
